import Vue from "vue";
import Router from "vue-router";
import LoginPage from "@/views/LoginPage.vue";
import DashboardPage from "@/views/DashboardPage.vue";
import CallDetailsPage from "@/views/CallDetailsPage.vue";
import ResetPasswordPage from "@/views/ResetPassword.vue";
import ForgotPasswordPage from "@/views/ForgotPasswordPage.vue";
import PageNotFound from "@/views/PageNotFound.vue";
import { deleteCurrentUser, getCurrentUser } from "@/helpers/helper";
import store from "@/store";
import {
  LOAD_ORGANIZATION,
  LOAD_PROJECT,
  INIT_PROJECT_CONTEXT,
  RESTORE_PROJECT_CONTEXT,
  VALIDATE_REMAINING_ENTRIES,
  ADD_ALERT,
} from "@/store/_actiontypes";
import { startTokenRefreshService } from "@/services/tokenRefreshService";

Vue.use(Router);

function loadActiveProject(activeEntry) {
  return Promise.all([
    store.dispatch(
      `account/${LOAD_PROJECT}`,
      { project_id: activeEntry.project_id },
      { root: true }
    ),
    store.getters["account/userProfile"]?.id
      ? null
      : store.dispatch(`account/${LOAD_ORGANIZATION}`, {}, { root: true }),
  ]);
}

const router = new Router({
  mode: "history",
  routes: [
    {
      path: "/login",
      name: "LoginPage",
      component: LoginPage,
      meta: { title: "Login | Maira Voice" },
    },
    {
      path: "/forgot-password",
      name: "ForgotPasswordPage",
      component: ForgotPasswordPage,
      meta: { title: "Forgot Password | Maira Voice" },
    },
    {
      path: "/reset-password/:token",
      name: "ResetPasswordPage",
      component: ResetPasswordPage,
      meta: { title: "Reset Password | Maira Voice" },
    },
    {
      path: "/404",
      name: "PageNotFound",
      component: PageNotFound,
      meta: { title: "Page Not Found | Maira Voice" },
    },
    {
      path: "/",
      name: "HomePage",
      component: DashboardPage,
      meta: { title: "Home | Maira Voice" },
    },
    {
      path: "/call",
      name: "CallDetailsPage",
      component: CallDetailsPage,
      meta: { title: "Info | Maira Voice" },
    },
    {
      path: "*",
      redirect: { name: "PageNotFound" },
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

router.beforeEach(async (to, from, next) => {
  const currentUser = getCurrentUser();
  const isPublicRoute = ["LoginPage", "ResetPasswordPage", "ForgotPasswordPage", "PageNotFound"].includes(to.name);

  // If not logged in and trying to access a protected route, redirect to login
  if (!isPublicRoute && !currentUser) {
    return next({ name: "LoginPage" });
  }

  // If logged in and trying to go to login page, redirect to home
  if (to.name === "LoginPage" && currentUser) {
    return next({ name: "HomePage" });
  }

  // ✅ If the user is logged in and navigating to a route (except LoginPage),
  // make sure to check project permission first then load organization data
  if (!isPublicRoute && currentUser) {
    try {
      let activeEntry = await store.dispatch(
        `project/${RESTORE_PROJECT_CONTEXT}`
      );

      if (!activeEntry) {
        activeEntry = await store.dispatch(`project/${INIT_PROJECT_CONTEXT}`, {
          use_stored_project: true,
        });
      }

      if (!activeEntry?.project_id) {
         throw new Error("ユーザーは、設定されたどのプロジェクトにもアクセスできません。");
       }

      try {
        await loadActiveProject(activeEntry);
      } catch (loadError) {
        activeEntry = await store.dispatch(`project/${INIT_PROJECT_CONTEXT}`, {
          use_stored_project: false,
          force: true,
        });
        if (!activeEntry?.project_id) throw loadError;
        await loadActiveProject(activeEntry);
      }

      // Keeps the token fresh instead of waiting for a request to fail with a
      // 401. Started here, not at boot, because a refresh has to know which
      // account is active. Does nothing if already running.
      startTokenRefreshService();

      // The switcher needs to know which other projects the user can open.
      // Left unawaited so it does not hold up the page; anything that needs the
      // answer awaits the same promise.
      store.dispatch(`project/${VALIDATE_REMAINING_ENTRIES}`).catch(() => {});
    } catch (error) {
      console.error("Error switching account:", error);
      store.dispatch(
        `alert/${ADD_ALERT}`,
        {
          message: "このMaira Voiceアプリケーションには招待されていません",
          color: "error",
        },
        { root: true }
      );
      deleteCurrentUser();
      // Optionally, redirect to login if switch fails
      return next({ name: "LoginPage" });
    }
  }

  // Proceed to route
  next();
});

function addDynamicPageTitle(to, from) {
  if (
    typeof to !== "object" ||
    !Array.isArray(to.matched) ||
    typeof from !== "object" ||
    !Array.isArray(from.matched)
  ) {
    throw new TypeError(
      "'to' and 'from' must be objects with 'matched' property of type Array"
    );
  }

  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find((r) => r.meta && r.meta.title);

  const previousNearestWithMeta = from.matched
    .slice()
    .reverse()
    .find((r) => r.meta && r.meta.metaTags);

  // If a route with a title was found, set the document (page) title to that value.
  if (nearestWithTitle) {
    document.title = nearestWithTitle.meta.title;
  } else if (previousNearestWithMeta) {
    document.title = previousNearestWithMeta.meta.title;
  }
}

router.afterEach((to, from, next) => {
  // Function for adding dynamic page title
  addDynamicPageTitle(to, from, next);
});

export default router;
